/**
 * 规则事件 → 看得见的飘字、读屏播报。纯函数：给事件和事件发生后的状态，交回要画的字与要念的话。
 * 用词只说游戏里发生了什么，不出现内部代码。
 */
import type { SnakeEventBody, SnakeState } from "../core";

export type FloatTone = "good" | "bad" | "info";

export interface FloatText {
  id: number;
  text: string;
  /** 场地坐标 */
  x: number;
  y: number;
  tone: FloatTone;
  bornMs: number;
}

/** 飘字停留多久：「变大了」「+10」两行一起飘时也来得及看清 */
export const FLOAT_MS = 1200;
/** 前 60% 的时间不透明，之后才淡出：一路淡下去的话，深色地面上刚飘起来就发灰看不清（c84a D0104-1） */
export const FLOAT_FADE_FROM = 0.6;

/** 飘字在寿命的第 age（0–1）处的不透明度 */
export function floatAlpha(age: number): number {
  if (age <= FLOAT_FADE_FROM) return 1;
  return Math.max(0, (1 - age) / (1 - FLOAT_FADE_FROM));
}

/** 一条事件要飘的字（在宠物头顶）；没有就是空数组。 */
export function floatsFor(event: SnakeEventBody, state: SnakeState, nowMs: number, nextId: () => number): FloatText[] {
  const at = (text: string, tone: FloatTone, lift = 0): FloatText => ({ id: nextId(), text, tone, x: state.pet.x, y: state.pet.y - state.pet.radius - 8 - lift, bornMs: nowMs });
  switch (event.type) {
    case "ate":
      return [at(`+${event.points}`, "good")];
    case "size_changed":
      return [at(event.to > event.from ? "变大了" : "变轻了", "info", 18)];
    case "blocked":
      return [at(event.reason === "too_big" ? "挤不过去" : "推不动", "info")];
    case "crate_broken":
      return [at(`+${event.points}`, "good")];
    case "hit":
      return [at("−1 ♥", "bad")];
    case "near_miss":
      return [at("好险", "info")];
    default:
      return [];
  }
}

export function liveFloats(floats: readonly FloatText[], nowMs: number): FloatText[] {
  return floats.filter((f) => nowMs - f.bornMs < FLOAT_MS);
}

/** 读屏播报：只念改变局面的事；擦身而过、暂停这类靠按钮本身的状态读出来，不另念。 */
export function announcementFor(event: SnakeEventBody, petName: string): string | null {
  switch (event.type) {
    case "started":
      return `开始了。带${petName}吃零食，别碰毛线球，还有 ${Math.round(event.durationMs / 1000)} 秒。`;
    case "ate":
      return event.food === "treat" ? `吃到零食，加 ${event.points} 分。` : `吃到叶子，加 ${event.points} 分。`;
    case "size_changed":
      return event.to > event.from ? `${petName}变大了。` : `${petName}变轻了。`;
    case "blocked":
      return event.reason === "too_big" ? "个子太大，挤不过窄口。" : "个子太小，推不动纸箱。";
    case "crate_broken":
      return `撞开纸箱，加 ${event.points} 分。`;
    case "hit":
      return event.heartsLeft > 0 ? `被毛线球撞到了，还剩 ${event.heartsLeft} 颗心。` : "被毛线球撞到了。";
    case "ended":
      if (event.reason === "time_up") return `时间到，一共 ${event.result.score} 分。`;
      if (event.reason === "out_of_hearts") return `心用完了，一共 ${event.result.score} 分。`;
      return null;
    default:
      return null;
  }
}

/** 剩余时间显示成「0:42」 */
export function clockText(remainingMs: number): string {
  const seconds = Math.max(0, Math.ceil(remainingMs / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}
